// src/creator/creatorTripsData.js
// ─────────────────────────────────────────────────────────────────────────────
// Creator Trip TEMPLATES (hand-authored for the demo, no AI).
//
// A Template is what a creator publishes once. Everything here is FIXED:
// stops, order, days, experiences, tips, inner-trip transport, preferred stays.
// Only these get filled dynamically per follower (see creatorTripEngine.js):
//   1. Getting there  (follower city -> entry point)  live route search
//   2. Getting back   (exit point -> follower city)    live route search
//   3. Stays          live hotel search, preferred hotel matched by name
//   4. Price          everything above + creator markup
//
// lock levels:  LOCKED (creator signature, never changes)
//               PREFERRED (book this if available, else same tier nearby)
//               FLEXIBLE (any good option, solver picks best value)
// ─────────────────────────────────────────────────────────────────────────────

const IMG = {
    hill: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=900&q=75",
    temple: "https://images.unsplash.com/photo-1567157577867-05ccb1388e66?w=900&q=75",
    river: "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=900&q=75",
    city: "https://images.unsplash.com/photo-1529253355930-ddbe423a2ac7?w=900&q=75",
    lake: "https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=900&q=75",
};

/* Follower origin cities offered in the "From" picker (lat/lng resolves the
   route search exactly like a Google-place selection does in SearchForm). */
export const ORIGIN_CITIES = [
    { name: "Indore", lat: 22.7196, lng: 75.8577 },
    { name: "Jabalpur", lat: 23.1815, lng: 79.9864 },
    { name: "Bengaluru", lat: 12.9716, lng: 77.5946 },
    { name: "Mumbai", lat: 19.076, lng: 72.8777 },
    { name: "Delhi", lat: 28.6139, lng: 77.209 },
    { name: "Pune", lat: 18.5204, lng: 73.8567 },
    { name: "Hyderabad", lat: 17.385, lng: 78.4867 },
    { name: "Kolkata", lat: 22.5726, lng: 88.3639 },
];

export const CREATORS = {
    safarwithanya: {
        handle: "safarwithanya",
        name: "Anya Mehra",
        avatar_initials: "AM",
        bio: "Budget-first trips across Bharat. Trains, buses, shared cabs, real homestays. Every trip here is one I've done myself.",
        followers: "412K",
        platform: "Instagram",
        insta_url: "https://www.instagram.com/",
        trips_booked: 1240,
        rating: 4.8,
        trip_slugs: ["spiti-circuit-7d", "kashi-prayagraj-4d", "hampi-3d"],
    },
};

export const TRIPS = {
    /* ────────────────────────────────────────────────────────────────────── */
    "spiti-circuit-7d": {
        slug: "spiti-circuit-7d",
        creator: "safarwithanya",
        title: "Spiti Circuit on a Budget",
        subtitle: "7 days · Delhi to Delhi · night buses, shared cabs, homestays",
        cover: IMG.hill,
        reel_url: "https://www.instagram.com/",
        reel_views: "2.1M",
        vibe: ["Budget", "Backpacker", "Mountains"],
        duration_days: 7,
        // Follower must reach the entry point by start_by on Day 1
        entry: { name: "Delhi (Majnu ka Tila)", city: "Delhi", lat: 28.7004, lng: 77.2276, start_by: "19:30" },
        // Return leg leaves the exit point on Day 1 + day_offset, after leave_after
        exit: { name: "Delhi (ISBT Kashmere Gate)", city: "Delhi", lat: 28.6675, lng: 77.2285, day_offset: 7, leave_after: "09:00" },
        // Kunzum pass closes late Oct, the engine warns outside this window
        season: { from: "06-01", to: "10-20", note: "Kunzum pass road usually closes after mid-to-late October." },
        creator_markup_pp: 1500,
        max_group: 12,
        days: [
            {
                day: 1, title: "Night bus to the mountains",
                blocks: [
                    { type: "MOVE", mode: "bus", lock: "PREFERRED", from: "Delhi", to: "Manali", depart: "20:00", duration: "13h", overnight: true, price_pp: 1450, label: "Volvo AC sleeper, Majnu ka Tila to Manali" },
                    { type: "TIP", text: "Eat at Majnu ka Tila before boarding. Laphing at the corner stalls is a must.", reel_ts: "0:08" },
                ],
            },
            {
                day: 2, title: "Old Manali, slow day",
                blocks: [
                    { type: "STAY", city: "Manali", nights: 1, lock: "PREFERRED", preferred: "Zostel Manali", tier_max_pn: 2500, fallback_pn: 1400, room_pp: 2 },
                    { type: "STOP", name: "Hadimba Temple", time: "11:00", lock: "LOCKED" },
                    { type: "EXPERIENCE", name: "Café hopping, Old Manali", time: "15:00", lock: "LOCKED", note: "Get the trout at the riverside café from the reel." },
                ],
            },
            {
                day: 3, title: "Over Kunzum to Kaza",
                blocks: [
                    { type: "MOVE", mode: "shared_cab", lock: "PREFERRED", from: "Manali", to: "Kaza", depart: "06:00", duration: "10h", price_pp: 1800, label: "Shared Tempo Traveller via Atal Tunnel and Kunzum" },
                    { type: "STOP", name: "Chandratal viewpoint (detour)", time: "12:00", lock: "LOCKED", note: "Ask the driver for the 20 min stop at Batal dhaba." },
                    { type: "STAY", city: "Kaza", nights: 3, lock: "PREFERRED", preferred: "Hotel Deyzor", tier_max_pn: 3000, fallback_pn: 1600, room_pp: 2 },
                ],
            },
            {
                day: 4, title: "Monasteries and the highest bridge",
                blocks: [
                    { type: "STOP", name: "Key Monastery", time: "08:00", lock: "LOCKED" },
                    { type: "STOP", name: "Kibber village", time: "11:00", lock: "LOCKED" },
                    { type: "EXPERIENCE", name: "Chicham bridge walk", time: "13:00", lock: "LOCKED" },
                    { type: "TIP", text: "Altitude day. Drink 3L water, skip the beer tonight.", reel_ts: "0:31" },
                ],
            },
            {
                day: 5, title: "Langza, Hikkim, Komic",
                blocks: [
                    { type: "STOP", name: "Langza Buddha and fossil hunting", time: "08:30", lock: "LOCKED" },
                    { type: "EXPERIENCE", name: "Post a card from the world's highest post office, Hikkim", time: "11:00", lock: "LOCKED" },
                    { type: "STOP", name: "Komic village", time: "13:00", lock: "LOCKED" },
                ],
            },
            {
                day: 6, title: "Back to Manali",
                blocks: [
                    { type: "MOVE", mode: "shared_cab", lock: "PREFERRED", from: "Kaza", to: "Manali", depart: "05:30", duration: "10h", price_pp: 1800, label: "Shared Tempo Traveller" },
                    { type: "STAY", city: "Manali", nights: 1, lock: "FLEXIBLE", tier_max_pn: 2500, fallback_pn: 1400, room_pp: 2 },
                ],
            },
            {
                day: 7, title: "Mall Road and night bus home",
                blocks: [
                    { type: "STOP", name: "Mall Road and Vashisht hot springs", time: "10:00", lock: "LOCKED" },
                    { type: "MOVE", mode: "bus", lock: "PREFERRED", from: "Manali", to: "Delhi", depart: "17:00", duration: "13h", overnight: true, price_pp: 1450, label: "Volvo AC sleeper, Manali to Delhi" },
                ],
            },
        ],
    },

    /* ────────────────────────────────────────────────────────────────────── */
    "kashi-prayagraj-4d": {
        slug: "kashi-prayagraj-4d",
        creator: "safarwithanya",
        title: "Kashi and Prayagraj with Parents",
        subtitle: "4 days · Varanasi to Varanasi · easy pace, family friendly",
        cover: IMG.river,
        reel_url: "https://www.instagram.com/",
        reel_views: "860K",
        vibe: ["Pilgrimage", "Family", "Culture"],
        duration_days: 4,
        entry: { name: "Varanasi", city: "Varanasi", lat: 25.3176, lng: 82.9739, start_by: "16:00" },
        exit: { name: "Varanasi", city: "Varanasi", lat: 25.3176, lng: 82.9739, day_offset: 3, leave_after: "14:00" },
        season: { from: "10-01", to: "03-31", note: "Best Oct to Mar. Summer afternoons are very hot." },
        creator_markup_pp: 900,
        max_group: 8,
        days: [
            {
                day: 1, title: "Arrive, Dashashwamedh aarti",
                blocks: [
                    { type: "STAY", city: "Varanasi", nights: 2, lock: "PREFERRED", preferred: "BrijRama", tier_max_pn: 6000, fallback_pn: 2800, room_pp: 2 },
                    { type: "EXPERIENCE", name: "Ganga aarti from a boat", time: "18:30", lock: "LOCKED", note: "Book the boat from Dashashwamedh ghat, not Assi, for the front view." },
                ],
            },
            {
                day: 2, title: "Sunrise boat and Kashi Vishwanath",
                blocks: [
                    { type: "EXPERIENCE", name: "Sunrise boat ride, Assi to Manikarnika", time: "05:30", lock: "LOCKED" },
                    { type: "STOP", name: "Kashi Vishwanath corridor", time: "09:00", lock: "LOCKED" },
                    { type: "EXPERIENCE", name: "Kachori-jalebi breakfast at the lane from the reel", time: "11:00", lock: "LOCKED" },
                    { type: "STOP", name: "Sarnath", time: "15:00", lock: "LOCKED" },
                ],
            },
            {
                day: 3, title: "Triveni Sangam, Prayagraj",
                blocks: [
                    { type: "MOVE", mode: "cab", lock: "PREFERRED", from: "Varanasi", to: "Prayagraj", depart: "06:00", duration: "3h", price_pp: 900, label: "Private sedan, AC" },
                    { type: "EXPERIENCE", name: "Boat to Triveni Sangam", time: "10:00", lock: "LOCKED" },
                    { type: "STOP", name: "Bade Hanuman ji, Anand Bhawan", time: "13:00", lock: "LOCKED" },
                    { type: "STAY", city: "Prayagraj", nights: 1, lock: "FLEXIBLE", tier_max_pn: 4000, fallback_pn: 2200, room_pp: 2 },
                ],
            },
            {
                day: 4, title: "Back to Varanasi, head home",
                blocks: [
                    { type: "MOVE", mode: "cab", lock: "PREFERRED", from: "Prayagraj", to: "Varanasi", depart: "08:00", duration: "3h", price_pp: 900, label: "Private sedan, AC" },
                    { type: "TIP", text: "Buy Banarasi paan and silk from the Godowlia shops before you leave.", reel_ts: "0:52" },
                ],
            },
        ],
    },

    /* ────────────────────────────────────────────────────────────────────── */
    "hampi-3d": {
        slug: "hampi-3d",
        creator: "safarwithanya",
        title: "Hampi Boulders and Ruins",
        subtitle: "3 days · Hosapete to Hosapete · cycles, coracles, sunsets",
        cover: IMG.temple,
        reel_url: "https://www.instagram.com/",
        reel_views: "1.3M",
        vibe: ["Heritage", "Backpacker", "Photography"],
        duration_days: 3,
        entry: { name: "Hosapete (Hospet)", city: "Hospet", lat: 15.2689, lng: 76.3909, start_by: "09:00" },
        exit: { name: "Hosapete (Hospet)", city: "Hospet", lat: 15.2689, lng: 76.3909, day_offset: 2, leave_after: "19:00" },
        season: { from: "10-01", to: "02-28", note: "Oct to Feb is cool enough for cycling." },
        creator_markup_pp: 700,
        max_group: 10,
        days: [
            {
                day: 1, title: "Royal enclosure on a cycle",
                blocks: [
                    { type: "STAY", city: "Hampi", nights: 2, lock: "PREFERRED", preferred: "Hampi Boulders", tier_max_pn: 3500, fallback_pn: 1800, room_pp: 2 },
                    { type: "STOP", name: "Virupaksha Temple", time: "10:00", lock: "LOCKED" },
                    { type: "EXPERIENCE", name: "Cycle the royal enclosure and Lotus Mahal", time: "12:00", lock: "LOCKED" },
                    { type: "EXPERIENCE", name: "Sunset at Hemakuta hill", time: "17:45", lock: "LOCKED" },
                ],
            },
            {
                day: 2, title: "Across the river",
                blocks: [
                    { type: "EXPERIENCE", name: "Coracle ride on the Tungabhadra", time: "08:00", lock: "LOCKED" },
                    { type: "STOP", name: "Vittala Temple and stone chariot", time: "10:30", lock: "LOCKED" },
                    { type: "STOP", name: "Anjanadri hill, 575 steps", time: "16:30", lock: "LOCKED", note: "Start by 4:30 to make the sunset." },
                ],
            },
            {
                day: 3, title: "Slow morning, head home",
                blocks: [
                    { type: "EXPERIENCE", name: "Breakfast at Mango Tree", time: "09:00", lock: "LOCKED" },
                    { type: "MOVE", mode: "cab", lock: "FLEXIBLE", from: "Hampi", to: "Hosapete", depart: "17:30", duration: "40m", price_pp: 250, label: "Auto or cab to Hosapete" },
                ],
            },
        ],
    },
};

export const getTrip = (slug) => TRIPS[slug] || null;
export const getCreator = (handle) => CREATORS[handle] || null;
export const getCreatorTrips = (handle) =>
    (CREATORS[handle]?.trip_slugs || []).map((s) => TRIPS[s]).filter(Boolean);
